import { ES_MAPPING_KEYS } from '@/components/EsSheet/data';
import {
  COF_COLUMNS,
  COF_VALID_VALUES,
  COF_FIXED_VALID_VALUES,
  DC_COLUMNS,
  DC_VALID_VALUES,
  DC_FIXED_VALID_VALUES,
  EDD_COLUMNS,
  EDD_VALID_VALUES,
  EDD_FIXED_VALID_VALUES,
  GOVERNING_COLUMNS,
  GOVERNING_VALID_VALUES,
  GOVERNING_FIXED_VALID_VALUES,
  MATERIAL_TYPE_COLUMNS,
  MATERIAL_TYPE_VALID_VALUES,
  MATERIAL_TYPE_FIXED_VALID_VALUES,
  RC_COLUMNS,
  RC_VALID_VALUES,
  RC_FIXED_VALID_VALUES,
} from '@/constants';
import { EsSheetData } from './UploadPanel/UploadPanel';

/**
 * Check if all required columns are mapped
 */
export const areColumnsMapped = (esSheet: EsSheetData | null): boolean => {
  if (!esSheet || !esSheet.mappings) return false;

  const requiredFields = ES_MAPPING_KEYS.filter((key) => key.required);
  const mappedKeys = Object.values(esSheet.mappings);

  // Check if all required fields are mapped
  return requiredFields.every((requiredField) => mappedKeys.includes(requiredField.key));
};

/**
 * Check if constrained columns exist in the data
 */
const hasConstrainedColumns = (esSheet: EsSheetData): boolean => {
  if (!esSheet.mappings) return false;

  const mappedKeys = Object.values(esSheet.mappings);

  // Check if any COF, DC, or EDD columns are mapped
  const hasCof = COF_COLUMNS.some((column) => {
    const field = ES_MAPPING_KEYS.find((k) => k.name === column);
    return field && mappedKeys.includes(field.key);
  });

  const hasDc = DC_COLUMNS.some((column) => {
    const field = ES_MAPPING_KEYS.find((k) => k.name === column);
    return field && mappedKeys.includes(field.key);
  });

  const hasEdd = EDD_COLUMNS.some((column) => {
    const field = ES_MAPPING_KEYS.find((k) => k.name === column);
    return field && mappedKeys.includes(field.key);
  });

  const hasGoverning = GOVERNING_COLUMNS.some((column) => {
    const field = ES_MAPPING_KEYS.find((k) => k.name === column);
    return field && mappedKeys.includes(field.key);
  });

  const hasMaterialType = MATERIAL_TYPE_COLUMNS.some((column) => {
    const field = ES_MAPPING_KEYS.find((k) => k.name === column);
    return field && mappedKeys.includes(field.key);
  });

  const hasRc = RC_COLUMNS.some((column) => {
    const field = ES_MAPPING_KEYS.find((k) => k.name === column);
    return field && mappedKeys.includes(field.key);
  });

  return hasCof || hasDc || hasEdd || hasGoverning || hasMaterialType || hasRc;
};

/**
 * Check if all constrained values are valid (either already valid or properly mapped)
 */
export const areValuesMapped = (esSheet: EsSheetData | null): boolean => {
  if (!esSheet) return false;

  // If there are no constrained columns in the data, value mapping is not needed
  if (!hasConstrainedColumns(esSheet)) return true;

  const { headers, data, mappings, valueMappings } = esSheet;

  // Get unique values for each constrained column and check if they're all valid
  for (let colIndex = 0; colIndex < headers.length; colIndex++) {
    const header = headers[colIndex];
    const targetKey = mappings?.[header];
    const systemField = ES_MAPPING_KEYS.find((k) => k.key === targetKey);

    if (!systemField) continue;

    const mappedColumnName = systemField.name;

    // Determine valid values for this column
    let validValues: string[] | null = null;
    if (mappedColumnName.includes('(COF)')) {
      validValues = COF_VALID_VALUES;
    } else if (mappedColumnName === 'Damage Class') {
      validValues = DC_VALID_VALUES;
    } else if (mappedColumnName === 'EDD Method') {
      validValues = EDD_VALID_VALUES;
    } else if (GOVERNING_COLUMNS.includes(mappedColumnName)) {
      validValues = GOVERNING_VALID_VALUES;
    } else if (MATERIAL_TYPE_COLUMNS.includes(mappedColumnName)) {
      validValues = MATERIAL_TYPE_VALID_VALUES;
    } else if (RC_COLUMNS.includes(mappedColumnName)) {
      validValues = RC_VALID_VALUES;
    }

    // If this column has constrained values, check all data values
    if (validValues) {
      for (const row of data) {
        const cellValue = row[colIndex];
        if (!cellValue || cellValue.trim() === '') continue;

        // Apply value mapping if it exists
        let finalValue = cellValue;
        if (valueMappings?.[mappedColumnName]?.[cellValue]) {
          finalValue = valueMappings[mappedColumnName][cellValue];
          if (finalValue === '__RETAIN__') continue;
        }

        // Check if the final value is valid
        if (!validValues.includes(finalValue)) {
          return false; // Found an invalid value
        }
      }
    }
  }

  return true; // All constrained values are valid
};

/**
 * Detect exceptions in the data
 */
export const hasExceptions = (esSheet: EsSheetData | null): boolean => {
  if (!esSheet || !esSheet.mappings) return false;

  const { headers, data, mappings, valueMappings } = esSheet;

  // Check for equipment tag duplicates
  const equipmentTagIndex = headers.findIndex((h) => {
    const targetKey = mappings[h];
    return targetKey === 'risk_equipment_tag';
  });

  if (equipmentTagIndex !== -1) {
    const equipmentTags = new Set<string>();
    for (const row of data) {
      const tag = row[equipmentTagIndex];
      if (tag) {
        if (equipmentTags.has(tag)) {
          return true; // Duplicate found
        }
        equipmentTags.add(tag);
      }
    }
  }

  // Check for missing required fields, invalid values, and type errors
  for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
    const row = data[rowIndex];

    for (let colIndex = 0; colIndex < headers.length; colIndex++) {
      const header = headers[colIndex];
      const cellValue = row[colIndex];
      const targetKey = mappings[header];
      const systemField = ES_MAPPING_KEYS.find((k) => k.key === targetKey);

      if (!systemField) continue;

      const mappedColumnName = systemField.name;

      // Check for missing required fields
      if (systemField.required && (!cellValue || cellValue.trim() === '')) {
        return true;
      }

      // Check for invalid values and type errors
      if (cellValue && cellValue.trim() !== '') {
        // Get valid values for this column (use FIXED values without N/A for exceptions)
        let validValues: string[] | null = null;
        if (mappedColumnName.includes('(COF)')) {
          validValues = COF_FIXED_VALID_VALUES;
        } else if (mappedColumnName === 'Damage Class') {
          validValues = DC_FIXED_VALID_VALUES;
        } else if (mappedColumnName === 'EDD Method') {
          validValues = EDD_FIXED_VALID_VALUES;
        } else if (GOVERNING_COLUMNS.includes(mappedColumnName)) {
          validValues = GOVERNING_FIXED_VALID_VALUES;
        } else if (MATERIAL_TYPE_COLUMNS.includes(mappedColumnName)) {
          validValues = MATERIAL_TYPE_FIXED_VALID_VALUES;
        } else if (RC_COLUMNS.includes(mappedColumnName)) {
          validValues = RC_FIXED_VALID_VALUES;
        }

        // Apply value mappings if they exist
        let finalValue = cellValue;
        if (valueMappings?.[mappedColumnName]?.[cellValue]) {
          finalValue = valueMappings[mappedColumnName][cellValue];
          if (finalValue === '__RETAIN__') continue;
        }

        // Check if value is valid (FIXED_VALID_VALUES don't include N/A)
        if (validValues && !validValues.includes(finalValue)) {
          return true;
        }

        // Check for type errors (numeric columns, but skip 'N/A')
        const numericColumns = ['Base POF Value', 'Transition Period'];
        if (numericColumns.some((col) => mappedColumnName.includes(col))) {
          if (finalValue !== 'N/A' && isNaN(Number(finalValue))) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

/**
 * Determine which step to navigate to based on data validation
 * Returns step index: 0=Upload, 1=MapColumns, 2=MapValues, 3=Exceptions, 4=Review
 */
export const getNextValidStep = (esSheet: EsSheetData | null): number => {
  if (!esSheet) return 0; // Upload step

  // Check columns first
  if (!areColumnsMapped(esSheet)) {
    return 1; // Map columns step
  }

  // Check values
  if (!areValuesMapped(esSheet)) {
    return 2; // Map values step
  }

  // Check for exceptions
  if (hasExceptions(esSheet)) {
    return 3; // Exceptions step
  }

  // All valid, go to review
  return 4; // Review step
};
